import * as React from 'react';
import { isEmpty, xor } from 'lodash';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';

import StatusPill from '../status-pill/StatusPill';
import FavoriteIcon from '../favorite-icon/FavoriteIcon';
import AutofillButton from '../autofill-button/AutofillButton';
import ProposalContextMenu from '../context-menu/ProposalContextMenu';

import { __t, applyTranslations } from 'utils/language';
import { DEFAULT_MEMBER_CUSTOM_CHANNELS } from 'modules/proposal/utils/creator-proposal-utils';

import type { ListContextMenu, ProposalCreatorProfile, ProposalListMemberStatus } from 'types';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';

import './proposal-tile-nonaproval-footer.scss';

applyTranslations(require('./proposal-tile-nonaproval-footer.i18n.json'));

const TILE_CHANNELS: Array<ProposalTileChannelType> = ['instagram', 'tiktok'];

type Props = {
  canResetPosts: boolean,
  canAutofillPosts: boolean,
  canSetMedia?: boolean,
  status: ProposalListMemberStatus,
  hideContextMenu?: boolean,
  creatorProfile: ProposalCreatorProfile,
  contextMenuProps?: ListContextMenu,
  canAddToFavorite?: boolean,
  onToggleFavorite?: () => void,
  isFavorite: boolean,
  canDelete?: boolean,
  onDelete?: () => void,
  onMediaReset?: () => void,
  onAutofill?: () => void,
  onStatusReset?: () => void,
  canSetCustomChannels: boolean,
  customChannels: Array<ProposalTileChannelType>,
  onCustomChannelChange: (channels: Array<ProposalTileChannelType>) => void
};

function getAvailableChannels(creatorProfile: ProposalCreatorProfile) {
  return DEFAULT_MEMBER_CUSTOM_CHANNELS
    .filter(channel => !isEmpty(creatorProfile.channels[channel]));
}

const NonApprovalModeFooter = (
  {
    canSetMedia,
    status,
    hideContextMenu,
    canDelete,
    creatorProfile,
    contextMenuProps,
    canAddToFavorite,
    onToggleFavorite,
    isFavorite,
    onDelete,
    onMediaReset,
    onAutofill,
    onStatusReset,
    canResetPosts,
    canAutofillPosts,
    canSetCustomChannels,
    customChannels = getAvailableChannels(creatorProfile),
    onCustomChannelChange = () => {}
  }: Props
): JSX.Element => {
  const availableChannels = TILE_CHANNELS
    .filter(channel => !isEmpty(creatorProfile.channels[channel]));

  return (
    <React.Fragment>
      {canSetCustomChannels && availableChannels.length > 1 && (
        <div className="proposal-tile-nonaproval-footer__channels">
          {
            availableChannels
              .map((option) => (
                <InputCheckbox
                  key={`custom-tile-channel--${option}-${creatorProfile.id}`}
                  name="custom-tile-channel"
                  value={option}
                  size="small"
                  id={`custom-tile-channel-option--${option}-${creatorProfile.id}`}
                  checked={customChannels.includes(option)}
                  disabled={customChannels.includes(option) && customChannels.length === 1}
                  label={__t(`proposal_tile_nonaproval_footer.channel_type_option.${option}`)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onCustomChannelChange(xor(customChannels, [event.currentTarget.value as ProposalTileChannelType]));
                  }}
                />
              ))
          }
        </div>
      )}

      {canSetMedia && canResetPosts && (
        <div className="proposal-tile-nonaproval-footer__reset-icon">
          <Tooltip
            message={__t('proposal_tile_nonaproval_footer.clear_images_label')}
            width="short"
          >
            <SVGDynamicIcon
              name="rotate-left-custom-fg"
              customColor="grey"
              width={24}
              height={24}
              onClick={onMediaReset}
            />
          </Tooltip>
        </div>
      )}

      <StatusPill status={status} />

      {canAutofillPosts && onAutofill && <AutofillButton onAutofill={onAutofill} />}

      <FavoriteIcon
        status={status}
        canAddToFavorite={canAddToFavorite}
        onToggleFavorite={onToggleFavorite}
        isFavorite={isFavorite}
      />

      {onStatusReset && (
        <div className="proposal-tile-nonaproval-footer__status-reset">
          <Tooltip
            message={__t('proposal_tile_nonaproval_footer.reset_approval_label')}
            width="short"
          >
            <SVGDynamicIcon
              name="rotate-left-custom-fg"
              customColor="grey"
              width={24}
              height={24}
              onClick={onStatusReset}
            />
          </Tooltip>
        </div>
      )}

      {!hideContextMenu && (
        <ProposalContextMenu
          canDelete={canDelete}
          onDelete={onDelete}
          creatorProfile={creatorProfile}
          contextMenuProps={contextMenuProps}
        />
      )}
    </React.Fragment>
  );
};

export default NonApprovalModeFooter;
